import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet, TrendingUp, AlertCircle, Calendar } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function AccountHolderDashboard() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyAccounts();
    fetchTransactions();
  }, []);

  const fetchMyAccounts = async () => {
    try {
      const data = await api.accountHolders.getMyAccounts();
      setAccounts(data);
      if (data.length > 0) {
        setSelectedAccount(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const data = await api.accountHolders.getMyTransactions();
      setTransactions(data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">Loading user data...</div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  // If no accounts found linked to this mobile
  if (accounts.length === 0) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-700">No Accounts Found</h2>
          <p className="text-gray-500 mt-2">We couldn't find any member accounts linked to your mobile number.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Use selected account data
  const savingsBalance = selectedAccount?.lavajam_account?.current_balance || 0;
  const activeLoan = selectedAccount?.active_loan;
  const pendingDues = activeLoan ? activeLoan.emi_amount : 0; // Simplified logic for now
  const nextPaymentDate = activeLoan ? formatDate(new Date().toISOString()) : 'No upcoming payments'; // Placeholder date logic

  const statCards = [
    {
      title: 'Savings Balance',
      value: formatCurrency(savingsBalance),
      icon: Wallet,
      color: 'bg-teal-100 text-teal-600',
      testId: 'stat-savings-balance',
    },
    {
      title: 'Active Loans',
      value: activeLoan ? '1' : '0',
      icon: TrendingUp,
      color: 'bg-blue-100 text-blue-600',
      testId: 'stat-active-loans',
    },
    {
      title: 'Pending Dues',
      value: activeLoan ? formatCurrency(pendingDues) : '₹0',
      icon: AlertCircle,
      color: 'bg-orange-100 text-orange-600',
      testId: 'stat-pending-dues',
    },
    {
      title: 'Next Payment',
      value: nextPaymentDate,
      icon: Calendar,
      color: 'bg-purple-100 text-purple-600',
      testId: 'stat-next-payment',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              Welcome, {selectedAccount ? formatFullName(selectedAccount.first_name, selectedAccount.middle_name, selectedAccount.last_name) : user.name}!
            </h1>
            <p className="text-gray-600">
              Account: <span className="font-mono font-medium text-emerald-600">{selectedAccount?.account_number}</span>
            </p>
          </div>

          {/* Account Switcher if multiple accounts exist */}
          {accounts.length > 1 && (
            <div className="bg-white p-2 rounded-lg shadow-sm border">
              <label className="text-xs text-gray-500 block mb-1">Switch Account</label>
              <select 
                className="text-sm border-none bg-transparent focus:ring-0 font-medium text-gray-700 cursor-pointer"
                value={selectedAccount?.id}
                onChange={(e) => {
                  const account = accounts.find(a => a.id === e.target.value);
                  setSelectedAccount(account);
                }}
              >
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {formatFullName(acc.first_name, acc.middle_name, acc.last_name)} ({acc.account_number})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title} className="glass card-hover" data-testid={card.testId}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 truncate">
                    {card.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg shrink-0 ${card.color}`}>
                    <Icon size={20} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold truncate">{card.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="glass col-span-2">
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Account</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.id} className="bg-white border-b hover:bg-gray-50">
                          <td className="px-4 py-3">{formatDate(txn.date)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              txn.type === 'Savings Deposit' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {txn.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{txn.account_number}</td>
                          <td className="px-4 py-3">{txn.description}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(txn.amount)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {txn.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No recent transactions found.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AccountHolderDashboard;
